import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusCircle, Search, RotateCcw } from 'lucide-react';
import { CATALOG_ENTRIES } from './syntheticData';
import {
  addConnection,
  applyTestResult,
  connectionToFormValues,
  deleteConnection,
  emptyFormValues,
  loadConnections,
  resetStoreToSeed,
  saveDiscoverySelections,
  setConnectionTesting,
  toggleConnectionActive,
  updateConnection,
} from './store';
import type {
  ConnectionFormValues,
  ConnectionStatus,
  McpCatalogEntry,
  McpServerConnection,
} from './types';
import ConnectionsList from './components/ConnectionsList';
import ConnectionForm from './components/ConnectionForm';
import CatalogBrowser from './components/CatalogBrowser';
import ConnectionDetail from './components/ConnectionDetail';
import './mcpResourcesV2.css';

type Tab = 'connections' | 'catalog';

type FormMode =
  | { kind: 'closed' }
  | { kind: 'create'; initial: ConnectionFormValues }
  | { kind: 'edit'; id: string; initial: ConnectionFormValues };

function catalogToFormValues(entry: McpCatalogEntry): ConnectionFormValues {
  const cfg = entry.defaultConfig;
  return {
    name: entry.name,
    description: entry.description,
    transport: cfg.transport ?? entry.transport,
    command: cfg.command ?? 'npx',
    argsText: (cfg.args ?? []).join('\n'),
    envText: cfg.env && Object.keys(cfg.env).length ? JSON.stringify(cfg.env, null, 2) : '',
    url: cfg.url ?? '',
    headersText:
      cfg.headers && Object.keys(cfg.headers).length ? JSON.stringify(cfg.headers, null, 2) : '',
    authType: cfg.authType ?? 'none',
    authValue: cfg.authValue ?? '',
    tagsText: (cfg.tags ?? entry.tags).join(', '),
    isActive: true,
    source: 'catalog',
    catalogId: entry.id,
  };
}

export default function MCPResourcesV2() {
  const [connections, setConnections] = useState<McpServerConnection[]>(() => loadConnections());
  const [tab, setTab] = useState<Tab>('connections');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConnectionStatus | 'all'>('all');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogTag, setCatalogTag] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<FormMode>({ kind: 'closed' });
  const [pendingTestAfterSave, setPendingTestAfterSave] = useState<string | null>(null);

  const selected = useMemo(
    () => connections.find((c) => c.id === selectedId) ?? null,
    [connections, selectedId]
  );

  const filteredConnections = useMemo(() => {
    const q = search.trim().toLowerCase();
    return connections.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!q) return true;
      return [c.name, c.description ?? '', c.transport, c.source, ...c.tags]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [connections, search, statusFilter]);

  const catalogTags = useMemo(() => {
    const set = new Set<string>();
    for (const e of CATALOG_ENTRIES) for (const t of e.tags) set.add(t);
    return Array.from(set).sort();
  }, []);

  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return CATALOG_ENTRIES.filter((e) => {
      if (catalogTag !== 'all' && !e.tags.includes(catalogTag)) return false;
      if (!q) return true;
      return [e.name, e.description, e.publisher, ...e.tags].join(' ').toLowerCase().includes(q);
    });
  }, [catalogSearch, catalogTag]);

  const runTest = useCallback((id: string) => {
    setConnections((prev) => setConnectionTesting(prev, id));
    window.setTimeout(() => {
      setConnections((prev) => applyTestResult(prev, id));
    }, 900);
  }, []);

  useEffect(() => {
    if (!pendingTestAfterSave) return;
    const exists = connections.some((c) => c.id === pendingTestAfterSave);
    if (!exists) return;
    const id = pendingTestAfterSave;
    setPendingTestAfterSave(null);
    setSelectedId(id);
    setTab('connections');
    const timer = window.setTimeout(() => runTest(id), 50);
    return () => window.clearTimeout(timer);
  }, [pendingTestAfterSave, connections, runTest]);

  const openCreate = () => {
    setFormMode({ kind: 'create', initial: emptyFormValues() });
  };

  const openEdit = (id: string) => {
    const conn = connections.find((c) => c.id === id);
    if (!conn) return;
    setFormMode({ kind: 'edit', id, initial: connectionToFormValues(conn) });
  };

  const openCatalogConfigure = (entry: McpCatalogEntry) => {
    setFormMode({ kind: 'create', initial: catalogToFormValues(entry) });
  };

  const handleSave = (values: ConnectionFormValues) => {
    if (formMode.kind === 'edit') {
      setConnections((prev) => updateConnection(prev, formMode.id, values));
      setFormMode({ kind: 'closed' });
      return;
    }
    if (formMode.kind === 'create') {
      setConnections((prev) => {
        const next = addConnection(prev, values);
        const created = next[0];
        if (created) setSelectedId(created.id);
        return next;
      });
      setTab('connections');
      setFormMode({ kind: 'closed' });
    }
  };

  const handleSaveAndTest = (values: ConnectionFormValues) => {
    if (formMode.kind === 'edit') {
      const id = formMode.id;
      setConnections((prev) => updateConnection(prev, id, values));
      setFormMode({ kind: 'closed' });
      setSelectedId(id);
      runTest(id);
      return;
    }
    if (formMode.kind === 'create') {
      let createdId = '';
      setConnections((prev) => {
        const next = addConnection(prev, values);
        createdId = next[0]?.id ?? '';
        return next;
      });
      setFormMode({ kind: 'closed' });
      setTab('connections');
      if (createdId) setPendingTestAfterSave(createdId);
    }
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Delete this MCP connection? This only affects local synthetic data.')) return;
    setConnections((prev) => deleteConnection(prev, id));
    if (selectedId === id) setSelectedId(null);
  };

  const handleResetSeed = () => {
    if (!window.confirm('Reset all V2 connections to the demo seed data?')) return;
    setConnections(resetStoreToSeed());
    setSelectedId(null);
  };

  if (selected) {
    return (
      <div className="mcp-v2">
        <ConnectionDetail
          connection={selected}
          onBack={() => setSelectedId(null)}
          onTest={() => runTest(selected.id)}
          onEdit={() => openEdit(selected.id)}
          onSaveSelections={(toolEnabled, resourceEnabled) => {
            setConnections((prev) =>
              saveDiscoverySelections(prev, selected.id, toolEnabled, resourceEnabled)
            );
          }}
        />
        <ConnectionForm
          open={formMode.kind !== 'closed'}
          title={formMode.kind === 'edit' ? 'Edit connection' : 'Add connection'}
          initial={formMode.kind === 'closed' ? emptyFormValues() : formMode.initial}
          onClose={() => setFormMode({ kind: 'closed' })}
          onSave={handleSave}
          onSaveAndTest={handleSaveAndTest}
        />
      </div>
    );
  }

  return (
    <div className="mcp-v2">
      <div className="mcp-v2__header">
        <div>
          <p className="mcp-v2__eyebrow">MCP</p>
          <h1 className="mcp-v2__title">MCP Resources V2</h1>
          <p className="mcp-v2__subtitle">
            Connect MCP servers, test them, and save discovered tools and resources. Frontend demo with
            synthetic data.
          </p>
        </div>
        <div className="mcp-v2__actions">
          <button type="button" className="mcp-v2__btn mcp-v2__btn--ghost" onClick={handleResetSeed}>
            <RotateCcw className="w-4 h-4" aria-hidden />
            Reset demo data
          </button>
          <button type="button" className="mcp-v2__btn mcp-v2__btn--primary" onClick={openCreate}>
            <PlusCircle className="w-4 h-4" aria-hidden />
            Add custom connection
          </button>
        </div>
      </div>

      <div className="mcp-v2__tabs" role="tablist" aria-label="MCP Resources V2 sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'connections'}
          className={`mcp-v2__tab${tab === 'connections' ? ' mcp-v2__tab--active' : ''}`}
          onClick={() => setTab('connections')}
        >
          My connections ({connections.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'catalog'}
          className={`mcp-v2__tab${tab === 'catalog' ? ' mcp-v2__tab--active' : ''}`}
          onClick={() => setTab('catalog')}
        >
          Browse catalog ({CATALOG_ENTRIES.length})
        </button>
      </div>

      {tab === 'connections' ? (
        <>
          <div className="mcp-v2__toolbar">
            <div className="mcp-v2__search">
              <Search aria-hidden />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search connections…"
                aria-label="Search connections"
              />
            </div>
            <select
              className="mcp-v2__select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ConnectionStatus | 'all')}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="connected">Connected</option>
              <option value="never_tested">Not tested</option>
              <option value="failed">Failed</option>
              <option value="testing">Testing</option>
            </select>
          </div>
          <ConnectionsList
            connections={filteredConnections}
            onOpen={setSelectedId}
            onEdit={openEdit}
            onDelete={handleDelete}
            onToggleActive={(id) => setConnections((prev) => toggleConnectionActive(prev, id))}
          />
        </>
      ) : (
        <>
          <div className="mcp-v2__toolbar">
            <div className="mcp-v2__search">
              <Search aria-hidden />
              <input
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                placeholder="Search MCP repositories…"
                aria-label="Search catalog"
              />
            </div>
            <select
              className="mcp-v2__select"
              value={catalogTag}
              onChange={(e) => setCatalogTag(e.target.value)}
              aria-label="Filter by tag"
            >
              <option value="all">All tags</option>
              {catalogTags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <CatalogBrowser entries={filteredCatalog} onConfigure={openCatalogConfigure} />
        </>
      )}

      <ConnectionForm
        open={formMode.kind !== 'closed'}
        title={
          formMode.kind === 'edit'
            ? 'Edit connection'
            : formMode.kind === 'create' && formMode.initial.source === 'catalog'
              ? 'Configure catalog server'
              : 'Add custom connection'
        }
        initial={formMode.kind === 'closed' ? emptyFormValues() : formMode.initial}
        onClose={() => setFormMode({ kind: 'closed' })}
        onSave={handleSave}
        onSaveAndTest={handleSaveAndTest}
      />
    </div>
  );
}
